import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import lodashKeyBy from 'lodash/fp/keyBy';

import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';

import {
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_NO_RESPONSE,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_NOT_INTERESTED,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_ACCEPTED_ANOTHER_LISTING,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED_BY_PARTNER,
  HOUSEHOLD_APPLICATION_GUARANTOR_STATUS_OPTION_ITEMS,
  HOUSEHOLD_LISTING_STATUS_ARCHIVED,
} from 'common/householdConstants';

import {
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_DATA_SOURCE_SMART_APARTMENT,
} from 'common/listingConstants';

import { STRIPE_CHARGE_STATUS_SUCCESS, STRIPE_CHARGE_STATUS_PENDING } from 'common/stripeConstants';

import { MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM } from 'common/marketingPartnerListingConstants';

import {
  getListingFullAddressString,
  getListingAgentPhone,
  makeListingAddressStreetDisplay,
  isListingTenantScreening,
} from 'common/listingHelpers';

import { getHouseholdListingWeakestLinkStatus } from 'common/householdHelpers';

import { getHouseholdQualification } from 'common/householdApplication/prequalificationHelpers';

import {
  makeApplicationStatusList,
  isApplicationPastStatus,
  getHouseholdEarliestApplicationStatus,
} from 'common/householdApplication/applicationHelpers';

import { formatPrice } from 'common/formatters';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import isBoolean from 'common/util/isBoolean';

import SrLink from 'client/components/SrLink/SrLink';
import SrTourCopyMlsNumber from 'client/components/SrTourCopyMlsNumber';
import SrCopyText from 'client/components/SrCopyText';
import SrApplicationStatusTableCell from 'client/components/SrApplicationStatusTableCell';
import SrApplicationOpenPhotosModalButton from 'client/components/SrApplicationOpenPhotosModalButton';
import SrTenantAlertApplicationViewer from 'client/components/SrTenantAlertApplicationViewer';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import SrHouseholdDownloadDocuments from 'client/components/SrHouseholdDownloadDocuments';
import SrHouseholdListingChangeListingModal from 'client/components/SrHouseholdListingChangeListingModal';
import SrHouseholdApplicationTypeform, {
  SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE,
  SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR,
} from 'client/components/SrHouseholdApplicationTypeform/SrHouseholdApplicationTypeform';
import SrBox from 'client/components/SrBox';
import SrButton, {
  SR_BUTTON_FONT_SIZE_SMALL,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrPartnerNotesModal from 'client/components/SrPartnerNotesModal';
import SrHouseholdBuyersAgentRow from 'client/components/SrHouseholdBuyersAgentRow';
import SrHouseholdListingUploadDraftLeaseModal from 'client/components/SrHouseholdListingUploadDraftLeaseModal';

const getIsQualifiedHouseholdListing = ({ household, applicationListing }) => {
  try {
    const householdQualification = getHouseholdQualification({
      moveInDate: household.renterMoveInDate,
      listing: applicationListing.listing,
      tenantScreeningCriteria: applicationListing.listing.tenantScreeningCriteria,
      renters: household.users,
      householdApplications: applicationListing.applications,
      householdGuarantors: household.guarantors,
      guarantorApplications: household.guarantorApplications,
      tenantAlertReports: household.tenantAlertReports,
      leadRenterId: household.leadRenter.id,
    });

    return householdQualification.isQualified;
  } catch (err) {
    return false;
  }
};

const keyByUserId = lodashKeyBy('userId');

const householdApplicationStatusesToDownloadFiles = makeApplicationStatusList({
  fromApplicationStatus: HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
});

const householdAcceptedAplicationStatusList = makeApplicationStatusList({
  fromApplicationStatus: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
});

const SrHouseholdApplicationPdfLink = ({
  webmergeTemplate,
  application,
  applicationSubmission,
  householdListingId,
  applicationRegeneratingDocs,
  onGenerateDocs,
}) => {
  if (
    webmergeTemplate.allowedApplicationTypes &&
    !webmergeTemplate.allowedApplicationTypes.includes(application.category)
  ) {
    return null;
  }
  const applicationRegeneratingDocsKey = [
    application.id,
    householdListingId,
    webmergeTemplate.category,
  ].join('-');
  const isRegeneratingDocs = applicationRegeneratingDocs[applicationRegeneratingDocsKey];
  const pdfLink = (applicationSubmission || application || {})[webmergeTemplate.pdfLinkField];

  if (isRegeneratingDocs) {
    return (
      <span style={{ paddingRight: '8px' }} className="link opacity-25">
        please wait...
      </span>
    );
  }

  if (pdfLink) {
    return (
      <span className="p-relative">
        <a target="_blank" rel="noopener noreferrer" href={pdfLink} className="link">
          {webmergeTemplate.label}
        </a>
        <span
          className="SrRegenerateButton"
          data-application-id={application.id}
          data-household-listing-id={householdListingId}
          data-category={webmergeTemplate.category}
          onClick={onGenerateDocs}
        />
      </span>
    );
  }

  return (
    <span
      style={{ paddingRight: '8px' }}
      className="link opacity-25"
      data-application-id={application.id}
      data-household-listing-id={householdListingId}
      data-category={webmergeTemplate.category}
      onClick={onGenerateDocs}
    >
      {webmergeTemplate.label}
    </span>
  );
};

SrHouseholdApplicationPdfLink.propTypes = {
  webmergeTemplate: PropTypes.object.isRequired,
  application: PropTypes.object.isRequired,
  householdListingId: PropTypes.number.isRequired,
  applicationRegeneratingDocs: PropTypes.object.isRequired,
  onGenerateDocs: PropTypes.func.isRequired,
};

class SrHouseholdDashboardCardListing extends Component {
  static propTypes = {
    applicationListing: PropTypes.object.isRequired,
    household: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    appUrl: PropTypes.string.isRequired,
    currentUser: PropTypes.object.isRequired,
    applicationRegeneratingDocs: PropTypes.object.isRequired,
    onPhotosModalOpenClick: PropTypes.func.isRequired,
    onGenerateDocs: PropTypes.func.isRequired,
    onApplicationStatusUpdate: PropTypes.func.isRequired,
    onGetGuarantorLinkClick: PropTypes.func.isRequired,
    onGetGuarantorApplicationPaymentLinkClick: PropTypes.func.isRequired,
    onGetSecurityDepositLinkClick: PropTypes.func.isRequired,
    onListingUnarchiveClick: PropTypes.func.isRequired,
    onListingArchiveClick: PropTypes.func.isRequired,
    onUpdateHousehold: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    openBuyersAgentModal: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isCollapsed: false,
      isPartnerNotesModalOpen: false,
      isHouseholdListingChangeListingModalOpen: false,
    };
  }

  UNSAFE_componentWillMount() {
    const { applicationListing } = this.props;
    const isArchived =
      applicationListing.householdListingStatus === HOUSEHOLD_LISTING_STATUS_ARCHIVED;
    this._setCollapseState({ isArchived });
  }

  _onCollapseActionClicked = () => {
    const { isCollapsed } = this.state;
    this.setState({ isCollapsed: !isCollapsed });
  };

  _setCollapseState = ({ isArchived }) => {
    const { isCollapsed } = this.state;
    if (isArchived && !isCollapsed) {
      this.setState({ isCollapsed: true });
    } else if (isArchived && isCollapsed) {
      this.setState({ isCollapsed: false });
    }
  };

  _onTogglePartnerNotesModal = () => {
    this.setState({
      isPartnerNotesModalOpen: !this.state.isPartnerNotesModalOpen,
    });
  };

  _onToggleHouseholdListingChangeListingModal = (e) => {
    if (e) {
      preventDefaultPropagation(e);
    }

    this.setState({
      isHouseholdListingChangeListingModalOpen: !this.state
        .isHouseholdListingChangeListingModalOpen,
    });
  };

  _onUpdateBuyersAgentClick = () => {
    const { applicationListing, openBuyersAgentModal } = this.props;
    openBuyersAgentModal(applicationListing);
  };

  _draftLeaseButtonGetLabel = (applicationListing) => {
    const { partner, securityDepositCharge } = applicationListing;

    if (
      partner?.earlyLeaseDraftingEnabled ||
      securityDepositCharge?.status === STRIPE_CHARGE_STATUS_SUCCESS
    ) {
      return 'DRAFT LEASE';
    }

    const label = 'DRAFT LEASE - Payment Pending';

    if (securityDepositCharge?.status === STRIPE_CHARGE_STATUS_PENDING) {
      const createdAt = momentWithAustinTimezone(securityDepositCharge.createdDate).format(
        'MMMM D [at] h:mma',
      );

      return `${label} (initiated ${createdAt})`;
    }

    return label;
  };

  _isDraftLeaseButtonEnabled = (applicationListing) => {
    if (applicationListing.partner?.earlyLeaseDraftingEnabled) {
      return true;
    }

    return applicationListing.securityDepositCharge?.status === STRIPE_CHARGE_STATUS_SUCCESS;
  };

  _getDraftLeaseButton = (applicationListing) => {
    const draftLeaseButtonLabel = this._draftLeaseButtonGetLabel(applicationListing);
    const draftLeaseButtonEnabled = this._isDraftLeaseButtonEnabled(applicationListing);

    return draftLeaseButtonEnabled ? (
      <SrLink target="_blank" to={`/admin/lease-draft/${applicationListing.householdListingId}`}>
        <SrButton fontSizeType={SR_BUTTON_FONT_SIZE_SMALL} theme={SR_BUTTON_THEME_ADOBE_BLUE}>
          {draftLeaseButtonLabel}
        </SrButton>
      </SrLink>
    ) : (
      <SrButton fontSizeType={SR_BUTTON_FONT_SIZE_SMALL} theme={SR_BUTTON_THEME_ADOBE_GREY}>
        {draftLeaseButtonLabel}
      </SrButton>
    );
  };

  render() {
    const {
      applicationListing,
      household,
      index,
      users,
      appUrl,
      currentUser,
      applicationRegeneratingDocs,
      onPhotosModalOpenClick,
      onGenerateDocs,
      onApplicationStatusUpdate,
      onListingUnarchiveClick,
      onListingArchiveClick,
      onGetGuarantorLinkClick,
      onGetSecurityDepositLinkClick,
      onGetGuarantorApplicationPaymentLinkClick,
      onUpdateHousehold,
      dispatch,
    } = this.props;

    const {
      isCollapsed,
      isPartnerNotesModalOpen,
      isHouseholdListingChangeListingModalOpen,
      isLeaseDraftModalOpen,
    } = this.state;

    const isArchived =
      applicationListing.householdListingStatus === HOUSEHOLD_LISTING_STATUS_ARCHIVED;

    const showDownloadAllFilesLink = householdApplicationStatusesToDownloadFiles.includes(
      getHouseholdListingWeakestLinkStatus({ householdListing: applicationListing }),
    );

    const showUploadLeaseDraftButton = applicationListing.applications.every((application) => {
      return isApplicationPastStatus(application, HOUSEHOLD_APPLICATION_STATUS_ACCEPTED);
    });

    const tenantAlertReportsByUserId = keyByUserId(household.tenantAlertReports);

    const showNoOneTouredHomeLabel =
      !isArchived &&
      isBoolean(applicationListing.householdListingInPersonTour) &&
      !applicationListing.householdListingInPersonTour &&
      !applicationListing.householdListingExistTourListingCompleted &&
      applicationListing.listing?.tenantScreeningCriteria?.requireInPersonTour;

    const showDraftLeaseButton =
      !isArchived &&
      applicationListing.applications.every((application) => {
        return isApplicationPastStatus(application, HOUSEHOLD_APPLICATION_STATUS_ACCEPTED);
      });

    const showAppProcessingButton =
      !isArchived &&
      applicationListing.applications.every((application) => {
        return isApplicationPastStatus(application, HOUSEHOLD_APPLICATION_STATUS_COMPLETED);
      });

    const householdApplicationStatus = getHouseholdEarliestApplicationStatus(
      applicationListing.applications,
    );
    const householdAccepted = householdAcceptedAplicationStatusList.includes(
      householdApplicationStatus,
    );

    const draftLeaseButton = this._getDraftLeaseButton(applicationListing);

    const isQualifiedHouseholdListing = getIsQualifiedHouseholdListing({
      household,
      applicationListing,
    });

    return (
      <div className={classnames('listing', index === 0 && 'first', isArchived && 'archived')}>
        <div className="listingTopInfo">
          <span className="address">
            <SrLink
              className={classnames('inlineFlex', isArchived && 'archived')}
              to={`/listing/${applicationListing.listing.id}`}
            >
              {isArchived
                ? '(Archived) ' + getListingFullAddressString(applicationListing.listing)
                : makeListingAddressStreetDisplay(applicationListing.listing)}
              {showNoOneTouredHomeLabel && (
                <SrFlex bgColor="red" borderRadius={2} marginLeft={10} padding="2px 9px 0">
                  <SrText font="whitney-sc" color="white" weight="bold">
                    no one toured
                  </SrText>
                </SrFlex>
              )}
              {applicationListing.listing.isShowUnit && (
                <SrFlex bgColor="orange300" borderRadius={2} marginLeft={10} padding="2px 9px 0">
                  <SrText font="whitney-sc" color="white" weight="bold">
                    show unit
                  </SrText>
                </SrFlex>
              )}
              {applicationListing.applications.some(({ isOnlineApp }) => isOnlineApp) && (
                <SrFlex bgColor="green" borderRadius={2} marginLeft={10} padding="2px 9px 0">
                  <SrText font="whitney-sc" color="white" weight="bold">
                    online app
                  </SrText>
                </SrFlex>
              )}
              {!isArchived && (
                <SrText
                  color="blue300"
                  weight="medium"
                  paddingLeft={10}
                  paddingTop={5}
                  onClick={this._onToggleHouseholdListingChangeListingModal}
                >
                  Change Listing
                </SrText>
              )}
              {isArchived && (
                <span className="tooltip">
                  {applicationListing.householdListingCancelReason ===
                  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED_BY_PARTNER // eslint-disable-line max-len
                    ? `Partner denial reason: ${applicationListing.householdListingRejectedReason}`
                    : applicationListing.householdListingRejectedReason
                    ? `Rejected: ${applicationListing.householdListingRejectedReason}`
                    : `Cancelled: ${applicationListing.householdListingCancelReason}`}
                </span>
              )}
            </SrLink>
            {showAppProcessingButton && (
              <SrLink
                to={`/admin/app-processing/${applicationListing.householdListingId}`}
                target="_blank"
              >
                <SrText color="blue300" weight="medium" verticalAlign="bottom" paddingLeft={10}>
                  App Processing Tool
                </SrText>
              </SrLink>
            )}
            {showAppProcessingButton && isQualifiedHouseholdListing && (
              <SrIcon className="household-processing" display="inlineBlock" />
            )}
            {!isArchived && (
              <SrText
                color="blue300"
                weight="medium"
                verticalAlign="bottom"
                paddingLeft={10}
                onClick={this._onUpdateBuyersAgentClick}
              >
                {applicationListing.hasBuyersAgent ? 'Update Agent' : 'Add Agent'}
              </SrText>
            )}
          </span>
          <div className="timestampContainer">
            {!isArchived && (
              <span className="timestamp">
                {`Created ${moment(applicationListing.householdListingCreatedDate).calendar()}`}
              </span>
            )}
            {isArchived && (
              <span
                className={classnames(
                  'timestamp',
                  'archived',
                  isCollapsed && 'collapsed',
                  !isCollapsed && 'notCollapsed',
                )}
                onClick={() => this._onCollapseActionClicked()}
              />
            )}
          </div>
          {!isCollapsed &&
            applicationListing.listing.listingAgreementSignedBy ===
              MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM && ( // eslint-disable-line max-len
              <SrBox marginTop="8px">
                <SrText color="orange300" size="16" weight="bold">
                  {applicationListing.listing.landlordId
                    ? 'Contact Owner: ' +
                      [
                        applicationListing.listing.landlordName,
                        applicationListing.listing.landlordPhone,
                        applicationListing.listing.landlordEmail,
                      ].join(', ')
                    : 'Owner info not found.'}
                </SrText>
              </SrBox>
            )}
          {!isCollapsed && (
            <div className="subhead">
              {applicationListing.listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT ? (
                <span>
                  <span className="detail">
                    {`SMART ${applicationListing.listing.smartApartmentId}`}
                  </span>
                  <span className="detail">
                    {`Front Office ${applicationListing.listing.apartmentPhone}`}
                  </span>
                  <span className="detail">{applicationListing.listing.apartmentName}</span>
                </span>
              ) : (
                <span>
                  <span className="detail">
                    {
                      applicationListing.listing.availableDate &&
                        `${moment(applicationListing.listing.availableDate).format(
                          'MM/DD/YYYY',
                        )} Available` // eslint-disable-line max-len
                    }
                  </span>
                  <span className="detail mlsDetail">
                    <span className="detail">MLS: </span>
                    <SrTourCopyMlsNumber mlsNumber={applicationListing.listing.mlsNumber} />
                  </span>
                  {getListingAgentPhone(applicationListing.listing) && (
                    <span className="detail">
                      {`LA: ${getListingAgentPhone(applicationListing.listing)}`}
                      <span className="tooltip">
                        {applicationListing.listing.listingAgentEmail}
                      </span>
                    </span>
                  )}
                </span>
              )}
              {applicationListing.listing.applicationDescription && (
                <span className="detail">{applicationListing.listing.applicationDescription}</span>
              )}
              <span className="detail">
                {`${formatPrice(applicationListing.listing.applicationFee)} Fee`}
              </span>
              <span className="detail">
                {formatPrice(
                  householdAccepted
                    ? applicationListing.householdListingRentPrice ||
                        applicationListing.listing.price
                    : applicationListing.listing.price,
                )}
              </span>
              {applicationListing.listing.listingManagementCompany && (
                <span className="detail">
                  <SrText
                    color="blue300"
                    weight="medium"
                    cursor="pointer"
                    onClick={this._onTogglePartnerNotesModal}
                  >
                    {applicationListing.listing.listingManagementCompany}
                  </SrText>
                </span>
              )}
              {applicationListing.listing.id && applicationListing.listing.partnerSlug && (
                <span className="detail">
                  <SrLink
                    to={
                      `/partner/${applicationListing.listing.partnerSlug}` +
                      `/listing/${applicationListing.listing.id}`
                    }
                    target="_blank"
                  >
                    <SrText color="blue300" weight="medium" cursor="pointer">
                      Partner Listing
                    </SrText>
                  </SrLink>
                </span>
              )}
              <span className="detail">
                <SrLink
                  to={`/admin/listings/edit/${applicationListing.listing.id}`}
                  target="_blank"
                >
                  <SrText color="blue300" weight="medium" cursor="pointer">
                    CLP
                  </SrText>
                </SrLink>
              </span>
              {applicationListing.listing.apartmentName && (
                <span className="detail">{applicationListing.listing.apartmentName}</span>
              )}
              {!isListingTenantScreening(applicationListing.listing) && (
                <div className="deleteContainer archive">
                  <span
                    className={classnames('actionLabel', isArchived && 'archived')}
                    onClick={isArchived ? onListingUnarchiveClick : null}
                    data-listing-id={applicationListing.listing.id}
                  >
                    {isArchived ? 'Unarchive' : 'Archive'}
                    {!isArchived && (
                      <div className="tooltip">
                        {applicationListing.householdListingRejectedReason}
                      </div>
                    )}
                  </span>
                  {!isArchived && (
                    <div className="dropdownContainer">
                      <span
                        className="option"
                        data-listing-id={applicationListing.listing.id}
                        data-reason={HOUSEHOLD_APPLICATION_ARCHIVE_REASON_ACCEPTED_ANOTHER_LISTING}
                        onClick={onListingArchiveClick}
                      >
                        accepted another listing
                      </span>
                      <span
                        className="option"
                        data-listing-id={applicationListing.listing.id}
                        data-reason={HOUSEHOLD_APPLICATION_ARCHIVE_REASON_NO_RESPONSE}
                        onClick={onListingArchiveClick}
                      >
                        lead unresponsive
                      </span>
                      <span
                        className="option"
                        data-listing-id={applicationListing.listing.id}
                        data-reason={HOUSEHOLD_APPLICATION_ARCHIVE_REASON_NOT_INTERESTED}
                        onClick={onListingArchiveClick}
                      >
                        not interested
                      </span>
                      <span
                        className="option"
                        data-listing-id={applicationListing.listing.id}
                        data-reason={HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED}
                        onClick={onListingArchiveClick}
                      >
                        rejected
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {!isCollapsed && applicationListing.hasBuyersAgent && (
            <SrHouseholdBuyersAgentRow
              buyersAgentName={applicationListing.buyersAgentName}
              buyersAgentPhone={applicationListing.buyersAgentPhone}
              buyersAgentEmail={applicationListing.buyersAgentEmail}
              onUpdateHousehold={onUpdateHousehold}
            />
          )}
        </div>
        {showDraftLeaseButton && <SrBox padding="0 20px 15px">{draftLeaseButton}</SrBox>}
        {!isCollapsed && (
          <div className="listingTable">
            <table>
              <thead>
                <tr>
                  <th className="renter">renter</th>
                  <th className="editApplication">edit app</th>
                  {!showDownloadAllFilesLink && (
                    <th className="downloadApplicationLinks">download</th>
                  )}
                  {showDownloadAllFilesLink && (
                    <th className="downloadApplicationLinks">
                      <SrHouseholdDownloadDocuments
                        householdId={household.id}
                        listingId={applicationListing.listing.id}
                        className="downloadAll"
                        appUrl={appUrl}
                      >
                        download docs
                      </SrHouseholdDownloadDocuments>
                    </th>
                  )}
                  {showUploadLeaseDraftButton ? (
                    <th
                      className="uploadLeaseDraft"
                      onClick={() => this.setState({ isLeaseDraftModalOpen: true })}
                    >
                      upload lease draft
                    </th>
                  ) : (
                    <th />
                  )}
                  <th className="applicationStage">application stage</th>
                  <th />
                  <th />
                  <th />
                  <th />
                  <th />
                  <th />
                </tr>
              </thead>
              <tbody>
                {applicationListing.applications.reduce((memo, application, index) => {
                  const user = users.find(({ id }) => id === application.userId);
                  const additionalDocuments = user.renterProfile
                    ? user.renterProfile.additionalDocuments || []
                    : [];
                  const generatedDocsDate = application.pdfDocumentsGeneratedDate
                    ? momentWithAustinTimezone(application.pdfDocumentsGeneratedDate).format('LLLL')
                    : null;

                  const shouldRenderEditApplicationStartedLink =
                    application.status === HOUSEHOLD_APPLICATION_STATUS_STARTED;
                  const shouldRenderEditApplicationCompletedLink =
                    application.status !== HOUSEHOLD_APPLICATION_STATUS_REQUESTED &&
                    application.status !== HOUSEHOLD_APPLICATION_STATUS_STARTED;

                  const tenantAlertReport = tenantAlertReportsByUserId[user.id];

                  memo.push(
                    <tr key={application.id}>
                      <td className="renter">
                        <SrCopyText text={user.name} />
                      </td>

                      <td
                        className={classnames('editApplication', {
                          redDot:
                            application.hasErrors &&
                            (shouldRenderEditApplicationStartedLink ||
                              shouldRenderEditApplicationCompletedLink),
                        })}
                      >
                        {shouldRenderEditApplicationStartedLink && (
                          <SrLink
                            to={`/admin/application/${application.userId}/${application.listingId}`}
                          >
                            Edit App
                          </SrLink>
                        )}
                        {shouldRenderEditApplicationCompletedLink && (
                          <SrLink
                            to={`/rental-application/${application.userId}/${application.listingId}/form`}
                          >
                            Edit App
                          </SrLink>
                        )}
                      </td>

                      <td className="downloadApplicationLinks">
                        <div className="downloadLinks">
                          <SrApplicationOpenPhotosModalButton
                            application={application}
                            additionalDocuments={additionalDocuments}
                            renterProfile={user.renterProfile || {}}
                            onPhotosModalOpenClick={onPhotosModalOpenClick}
                          />
                          {tenantAlertReport && (
                            <div className="SrTenantAlertApplicationViewerWrapper">
                              <SrTenantAlertApplicationViewer
                                user={user}
                                tenantAlertReport={tenantAlertReport}
                                onUpdateHousehold={onUpdateHousehold}
                              >
                                <SrText color="blue300" weight="medium" cursor="pointer">
                                  Credit
                                </SrText>
                              </SrTenantAlertApplicationViewer>
                            </div>
                          )}
                          <SrHouseholdApplicationTypeform
                            typeformId={SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE}
                            renterProfile={user.renterProfile || {}}
                            householdApplication={application}
                            dispatch={dispatch}
                            onUpdateHousehold={onUpdateHousehold}
                            incomeVerification={application.incomeVerification}
                            employmentVerification={application.employmentVerification}
                            acceptBankDataAsVoe={applicationListing.partner?.acceptBankDataAsVoe}
                          />
                          <SrHouseholdApplicationTypeform
                            typeformId={SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR}
                            renterProfile={user.renterProfile || {}}
                            householdApplication={application}
                            dispatch={dispatch}
                            onUpdateHousehold={onUpdateHousehold}
                            tenantAlertReport={tenantAlertReport}
                            tenantScreeningCriteria={
                              applicationListing.listing.tenantScreeningCriteria
                            }
                          />

                          {applicationListing.listing.webmergeTemplates.map((webmergeTemplate) => (
                            <SrHouseholdApplicationPdfLink
                              key={webmergeTemplate.id}
                              webmergeTemplate={webmergeTemplate}
                              application={application}
                              householdListingId={applicationListing.householdListingId}
                              applicationRegeneratingDocs={applicationRegeneratingDocs}
                              onGenerateDocs={onGenerateDocs}
                            />
                          ))}
                          {generatedDocsDate && (
                            <div className="tooltip">{`Generated on ${generatedDocsDate}`}</div>
                          )}
                        </div>
                      </td>
                      <SrApplicationStatusTableCell
                        application={application}
                        listing={applicationListing.listing}
                        disabled={
                          isArchived ||
                          application.status === HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED
                        }
                        onChange={onApplicationStatusUpdate}
                      />
                      {!user.householdGuarantor && (
                        <td>
                          <span
                            className="blue-link"
                            style={{ paddingLeft: '10px' }}
                            onClick={onGetGuarantorLinkClick}
                            data-renter-id={user.id}
                            data-renter-name={user.name}
                            data-renter-phone={user.phone}
                            data-listing-address={applicationListing.listing.addressStreetDisplay}
                            data-listing-id={applicationListing.listing.id}
                          >
                            Guarantor Link
                          </span>
                        </td>
                      )}
                      {!applicationListing.householdListingSecurityDepositStripeChargeId &&
                        (applicationListing.listing.partnerStripeAccountId ||
                          applicationListing.listing.stripeAccountId) &&
                        applicationListing.listing.dataSource === LISTING_DATA_SOURCE_SUNROOM &&
                        index === 0 && (
                          <td>
                            <span
                              className="green-link"
                              style={{ paddingLeft: '10px' }}
                              onClick={onGetSecurityDepositLinkClick}
                              data-listing-id={applicationListing.listing.id}
                            >
                              Deposit Link
                            </span>
                          </td>
                        )}
                    </tr>,
                  );

                  const renterGuarantors = household.householdGuarantors
                    .filter(({ renterUserId }) => renterUserId === user.id)
                    .map(({ guarantorUserId }) => users.find(({ id }) => id === guarantorUserId));

                  for (const renterGuarantor of renterGuarantors) {
                    const guarantorApplication = household.guarantorApplications.find(
                      ({ userId }) => userId === renterGuarantor.id,
                    );

                    const guarantorApplicationSubmission = !guarantorApplication
                      ? null
                      : household.householdApplicationSubmissions.find(
                          (householdApplicationSubmission) =>
                            householdApplicationSubmission.applicationId ===
                              guarantorApplication.id &&
                            householdApplicationSubmission.listingId ===
                              applicationListing.listing.id,
                        );

                    const generatedDocsDate =
                      guarantorApplicationSubmission &&
                      guarantorApplicationSubmission.pdfDocumentsGeneratedDate
                        ? momentWithAustinTimezone(
                            guarantorApplicationSubmission.pdfDocumentsGeneratedDate,
                          ).format('LLLL')
                        : null;

                    const tenantAlertReport = tenantAlertReportsByUserId[renterGuarantor.id];

                    memo.push(
                      <tr key={`${renterGuarantor.id}-${application.id}`}>
                        <td className="renter">
                          <div className="guarantorIcon" />
                          <SrCopyText text={renterGuarantor.name} />
                        </td>

                        <td className="editApplication">
                          {guarantorApplication && (
                            <SrLink
                              to={
                                `/rental-application/${guarantorApplication.userId}/` +
                                `${applicationListing.listing.id}/form`
                              }
                            >
                              Edit App
                            </SrLink>
                          )}
                        </td>
                        <td className="downloadApplicationLinks">
                          {guarantorApplication && (
                            <div className="downloadLinks">
                              <SrApplicationOpenPhotosModalButton
                                application={guarantorApplication}
                                additionalDocuments={
                                  (renterGuarantor.renterProfile || {}).additionalDocuments || []
                                }
                                renterProfile={renterGuarantor.renterProfile || {}}
                                onPhotosModalOpenClick={onPhotosModalOpenClick}
                              />
                              {tenantAlertReport && (
                                <div className="SrTenantAlertApplicationViewerWrapper">
                                  <SrTenantAlertApplicationViewer
                                    user={renterGuarantor}
                                    tenantAlertReport={tenantAlertReport}
                                    onUpdateHousehold={onUpdateHousehold}
                                  >
                                    <SrText color="blue300" weight="medium" cursor="pointer">
                                      Credit
                                    </SrText>
                                  </SrTenantAlertApplicationViewer>
                                </div>
                              )}
                              <SrHouseholdApplicationTypeform
                                typeformId={SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOE}
                                renterProfile={renterGuarantor.renterProfile || {}}
                                householdApplication={guarantorApplication}
                                dispatch={dispatch}
                                onUpdateHousehold={onUpdateHousehold}
                                incomeVerification={guarantorApplication.incomeVerification}
                                employmentVerification={guarantorApplication.employmentVerification}
                                acceptBankDataAsVoe={
                                  applicationListing.partner?.acceptBankDataAsVoe
                                }
                              />
                              <SrHouseholdApplicationTypeform
                                typeformId={SR_HOUSEHOLD_APPLICATION_TYPEFORM_ID_VOR}
                                renterProfile={renterGuarantor.renterProfile || {}}
                                householdApplication={guarantorApplication}
                                dispatch={dispatch}
                                onUpdateHousehold={onUpdateHousehold}
                                tenantAlertReport={tenantAlertReport}
                                tenantScreeningCriteria={
                                  applicationListing.listing.tenantScreeningCriteria
                                }
                              />
                              {applicationListing.listing.webmergeTemplates.map(
                                (webmergeTemplate) => (
                                  <SrHouseholdApplicationPdfLink
                                    key={webmergeTemplate.id}
                                    webmergeTemplate={webmergeTemplate}
                                    application={guarantorApplication}
                                    applicationSubmission={guarantorApplicationSubmission}
                                    householdListingId={applicationListing.householdListingId}
                                    applicationRegeneratingDocs={applicationRegeneratingDocs}
                                    onGenerateDocs={onGenerateDocs}
                                  />
                                ),
                              )}
                              {generatedDocsDate && (
                                <div className="tooltip">{`Generated on ${generatedDocsDate}`}</div>
                              )}
                            </div>
                          )}
                        </td>
                        {guarantorApplication && (
                          <td className="SrApplicationStatusTableCell">
                            <select
                              value={guarantorApplication.status}
                              onChange={(e) => {
                                const applicationStatus = e.target.value;
                                onApplicationStatusUpdate({
                                  applicationStatus: applicationStatus,
                                  applicationId: guarantorApplication.id,
                                  listingId: applicationListing.listing.id,
                                  householdId: household.id,
                                });
                              }}
                            >
                              {HOUSEHOLD_APPLICATION_GUARANTOR_STATUS_OPTION_ITEMS.map((
                                householdApplicationStatus, // eslint-disable-line max-len
                              ) => (
                                <option
                                  key={householdApplicationStatus.value}
                                  value={householdApplicationStatus.value}
                                >
                                  {householdApplicationStatus.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        )}
                        {guarantorApplication &&
                          (!guarantorApplicationSubmission ||
                            !guarantorApplicationSubmission.stripeChargeId) && (
                            <td>
                              <span
                                className="blue-link"
                                style={{ paddingLeft: '10px' }}
                                onClick={onGetGuarantorApplicationPaymentLinkClick}
                                data-application-id={guarantorApplication.id}
                                data-listing-id={applicationListing.listing.id}
                                data-phone={renterGuarantor.phone}
                              >
                                Get Application Payment Link
                              </span>
                            </td>
                          )}
                      </tr>,
                    );
                  }

                  return memo;
                }, [])}
              </tbody>
            </table>
          </div>
        )}
        {isPartnerNotesModalOpen && (
          <SrPartnerNotesModal
            partnerId={applicationListing.listing.partnerId}
            dispatch={dispatch}
            currentUser={currentUser}
            onClose={this._onTogglePartnerNotesModal}
          />
        )}
        {isHouseholdListingChangeListingModalOpen && (
          <SrHouseholdListingChangeListingModal
            household={household}
            householdListing={applicationListing}
            dispatch={dispatch}
            onUpdateHousehold={onUpdateHousehold}
            onClose={this._onToggleHouseholdListingChangeListingModal}
          />
        )}
        {isLeaseDraftModalOpen && (
          <SrHouseholdListingUploadDraftLeaseModal
            householdId={household.id}
            listingId={applicationListing.listing.id}
            leaseDraftPdfUrl={applicationListing.householdListingLeaseDraftUrl}
            dispatch={dispatch}
            onClose={() => this.setState({ isLeaseDraftModalOpen: false })}
          />
        )}
      </div>
    );
  }
}

SrHouseholdDashboardCardListing.displayName = 'SrHouseholdDashboardCardListing';

export default SrHouseholdDashboardCardListing;
